import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../button/Button";
import styles from "./CreatePasswordForm.module.scss";
import eye from "../../assets/icons/eye.svg";
import eyeHover from "../../assets/icons/eye-hover.svg";
import eyeSlash from "../../assets/icons/eye-slash.svg";
import eyeSlashHover from "../../assets/icons/eye-slash-hover.svg";
import dot from "../../assets/icons/circle.svg";
import done from "../../assets/icons/status-done.svg";
import like from "../../assets/icons/like.svg";
import Input from "../input/Input";
import { useCreateFirstLoginMutation, useCreateNewPasswordMutation } from "../../store/services/auth-api";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { logout } from "../../store/reducers/auth-reducer";
import { useTranslation } from "react-i18next";
import { getAccessToken } from "../../utils";
import { trackEvent } from "../../utils/analytics";
import { EVENT_NAMES } from "../../types/event-names";

interface CreatePasswordFormProps {
  text?: string;
  setFirstLogin?: (val: boolean) => void;
  expiredId?: string;
}

const CreatePasswordForm: React.FC<CreatePasswordFormProps> = ({
  text = "Create password",
  setFirstLogin,
  expiredId,
}) => {
  const { t } = useTranslation();
  useEffect(() => {
    trackEvent(EVENT_NAMES.FIRST_LOGIN_START);
    trackEvent(EVENT_NAMES.FIRST_LOGIN_PASSWORD_OPENED);
  }, []);

  const [passwordFocused, setPasswordFocused] = useState(false);
  const [passwordValue, setPasswordValue] = useState("");
  const [confirmFocused, setConfirmFocused] = useState(false);
  const [confirmValue, setConfirmValue] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordMismatch, setPasswordMismatch] = useState(false);
  const [createdPassword, setCreatedPassword] = useState(false);
  const [serverError, setServerError] = useState("");

  const navigate = useNavigate();

  const [minLength, setMinLength] = useState(false);
  const [hasUppercase, setHasUppercase] = useState(false);
  const [hasLowercase, setHasLowercase] = useState(false);
  const [hasDigit, setHasDigit] = useState(false);
  const [createFirstPassword] = useCreateFirstLoginMutation();
  const [createNewPassword] = useCreateNewPasswordMutation();
  const user = useAppSelector((state) => state.auth.user);
  const [isLoader, setIsLoader] = useState(false);
  const [passwordHover, setPasswordHover] = useState(false);
  const [confirmHover, setConfirmHover] = useState(false);

  const passwordEyeIcon = showPassword ? (passwordHover ? eyeSlashHover : eyeSlash) : passwordHover ? eyeHover : eye;

  const confirmEyeIcon = showConfirm ? (confirmHover ? eyeSlashHover : eyeSlash) : confirmHover ? eyeHover : eye;

  const dispacth = useAppDispatch();

  const checkPasswordRequirements = (value: string) => {
    setMinLength(value.length >= 8);
    setHasUppercase(/[A-Z]/.test(value));
    setHasLowercase(/[a-z]/.test(value));
    setHasDigit(/\d/.test(value));
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  const toggleConfirmVisibility = () => {
    setShowConfirm(!showConfirm);
  };

  const handleConfirmChange = (e: any) => {
    setConfirmValue(e.target.value);
    setPasswordMismatch(e.target.value !== passwordValue);
  };

  const handlePasswordChange = (e: any) => {
    setPasswordValue(e.target.value);
    checkPasswordRequirements(e.target.value);
  };

  const handleSubmitChange = async () => {
    setIsLoader(true);
    if (expiredId) {
      createNewPassword({
        id: expiredId,
        password1: passwordValue,
        password2: confirmValue,
        user: user,
        token: getAccessToken("accessToken"),
        email: user?.email,
      })
        .unwrap()
        .then((payload) => {
          //data is the result status
          setIsLoader(false);
          setCreatedPassword(true);
          //TODO: send email that password was changed
        })
        .catch((error) => {
          setIsLoader(false);
          setServerError(error.data.data);
          //TODO: Handle error. the message is in .data
        });
    } else {
      await createFirstPassword({
        password1: passwordValue,
        password2: confirmValue,
        user: user,
        token: getAccessToken("accessToken"),
        email: user?.email,
      })
        .unwrap()
        .then((payload) => {
          //data is the result status
          setIsLoader(false);
          setCreatedPassword(true);
          trackEvent(EVENT_NAMES.FIRST_LOGIN_PASSWORD_SUCCESS);
          sessionStorage.firstLogin = true;
        })
        .catch((error) => {
          setIsLoader(false);
          setServerError(error.data.data);
          //TODO: Handle error. the message is in .data
        });
    }

    //setCreatedPassword(true);
  };

  return (
    <>
      {createdPassword ? (
        <div className={styles.modal}>
          <img src={like} alt="like" className={styles.modal_img} />

          <p className={styles.modal_title}>
            {expiredId ? t("Password Successfully changed") : t(`Password Created Successfully`)}
          </p>
          <p className={styles.modal_subtitle}>
            {t("You can now access your Fortect account with your new password.")}
          </p>
          <div className={styles.modal_footer}>
            <Button
              onClick={() => {
                //change the main state back to login
                if (setFirstLogin) setFirstLogin(false);
                else dispacth(logout());
                navigate("/login");
              }}
              text={t("Login")}
              classNames={styles.modal_footer_btn}
            />
          </div>
        </div>
      ) : (
        <div className={styles.root}>
          <div className={styles.root_header}>
            <h1 className={styles.root_header_title}>{t("Create your new password")}</h1>
            <h2 className={styles.root_header_subtitle}>{t("Enter a new password for the account")}</h2>
          </div>
          <div className={styles.root_main}>
            <div className={styles.root_main_block}>
              <Input
                type={showPassword ? "text" : "password"}
                value={passwordValue}
                onChange={handlePasswordChange}
                onFocus={() => setPasswordFocused(true)}
                onBlur={() => setPasswordFocused(false)}
                focused={passwordFocused}
                placeholder={t("Enter new Password")}
              />
              <img
                src={passwordEyeIcon}
                alt="eye"
                className={styles.root_main_block_eye}
                onClick={togglePasswordVisibility}
                onMouseEnter={() => setPasswordHover(true)}
                onMouseLeave={() => setPasswordHover(false)}
              />
            </div>
            <div className={styles.root_main_block}>
              <Input
                type={showConfirm ? "text" : "password"}
                value={confirmValue}
                onChange={handleConfirmChange}
                onFocus={() => setConfirmFocused(true)}
                onBlur={() => setConfirmFocused(false)}
                focused={confirmFocused}
                placeholder={t("Confirm new Password")}
                error={passwordMismatch}
              />
              <img
                src={confirmEyeIcon}
                alt="eye"
                className={styles.root_main_block_eye}
                onClick={toggleConfirmVisibility}
                onMouseEnter={() => setConfirmHover(true)}
                onMouseLeave={() => setConfirmHover(false)}
              />
              {passwordMismatch ? (
                <p className={styles.root_main_error}>{t("Password must match")}.</p>
              ) : serverError !== "" ? (
                <p className={styles.root_main_error}>{serverError}</p>
              ) : (
                confirmValue && <p className={styles.root_main_successful}>{t("Password match.")}</p>
              )}
            </div>

            <div className={styles.root_main_rules}>
              <p className={styles.root_main_rules_title}>{t("Your new password should include:")}</p>
              <div className={styles.root_main_rules_list}>
                <div className={styles.root_main_rules_list_item}>
                  <div className={styles.root_main_rules_list_item_img}>
                    <img src={minLength ? done : dot} alt="dot" />
                  </div>

                  <p>{t("At least 8 characters")}</p>
                </div>
                <div className={styles.root_main_rules_list_item}>
                  <div className={styles.root_main_rules_list_item_img}>
                    <img src={hasUppercase ? done : dot} alt="dot" />
                  </div>

                  <p>{t("An uppercase letter")}</p>
                </div>
                <div className={styles.root_main_rules_list_item}>
                  <div className={styles.root_main_rules_list_item_img}>
                    <img src={hasLowercase ? done : dot} alt="dot" />
                  </div>

                  <p>{t("A lowercase letter")}</p>
                </div>
                <div className={styles.root_main_rules_list_item}>
                  <div className={styles.root_main_rules_list_item_img}>
                    <img src={hasDigit ? done : dot} alt="dot" />
                  </div>

                  <p>{t("A digit")}</p>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.root_footer}>
            <Button
              onClick={() => {
                if (
                  minLength &&
                  hasUppercase &&
                  hasLowercase &&
                  hasDigit &&
                  !passwordMismatch &&
                  passwordValue === confirmValue
                ) {
                  handleSubmitChange();
                  //setCreatedPassword(true);
                }
              }}
              text={t(text)}
              classNames={styles.root_footer_button}
              isLoader={isLoader}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default CreatePasswordForm;
